import { useEffect, useMemo, useRef, useState } from 'react';
import { useSocket } from '../../context/socket';
import { RADIO_QUEUE_KINDS } from './hitLabRadioTracks';

export const HITLAB_RADIO_LIVE_SUBSCRIBE_EVENT = 'hitlab:radio:live:subscribe';
export const HITLAB_RADIO_LIVE_UNSUBSCRIBE_EVENT = 'hitlab:radio:live:unsubscribe';
export const HITLAB_RADIO_TRACK_JOIN_EVENT = 'hitlab:radio:track:join';
export const HITLAB_RADIO_TRACK_LEAVE_EVENT = 'hitlab:radio:track:leave';
export const HITLAB_RADIO_SIGNAL_EVENT = 'hitlab:radio:signal';

const SIGNAL_TYPES = new Set([
  'play_count_updated',
  'qualified_listen',
  'feedback_landed',
  'support_landed',
  'artist_connected',
  'support_ready',
  'new_in_rotation',
  'rotation_refreshed',
  'listening_now_updated',
  'radio_station_listeners_updated',
  'radio_room_presence_updated',
]);

const CATALOG_REFRESH_SIGNAL_TYPES = new Set([
  'play_count_updated',
  'qualified_listen',
  'artist_connected',
  'support_ready',
  'new_in_rotation',
  'rotation_refreshed',
]);

const MOVEMENT_SIGNAL_TYPES = new Set([
  'play_count_updated',
  'qualified_listen',
  'feedback_landed',
  'support_landed',
  'artist_connected',
  'support_ready',
  'new_in_rotation',
  'rotation_refreshed',
]);

const REFRESH_THROTTLE_MS = 4000;
const SIGNAL_TTL_MS = 12000;

const cleanText = (value, max = 256) =>
  String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, max);

const toNonNegativeInteger = (value, fallback = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return Math.floor(parsed);
};

const normalizeIso = (value) => {
  const date = value ? new Date(value) : new Date();
  return Number.isFinite(date.getTime()) ? date.toISOString() : new Date().toISOString();
};

const normalizePlayerHandle = (value) => cleanText(value, 120);

const normalizeListenerPreview = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => {
      const playerId = normalizePlayerHandle(item?.playerId || item?.handle);
      if (!playerId) return null;
      return {
        playerId,
        displayName: normalizePlayerHandle(item?.displayName || playerId),
        profilePhoto: cleanText(item?.profilePhoto, 2048),
      };
    })
    .filter(Boolean)
    .slice(0, 5);

export const normalizeJgRadioLiveSignal = (signal = {}) => {
  const type = cleanText(signal.type, 80);
  if (!SIGNAL_TYPES.has(type)) return null;
  const trackId = cleanText(signal.trackId, 120);
  const payload = {
    type,
    at: normalizeIso(signal.at),
  };
  if (trackId) payload.trackId = trackId;

  const title = cleanText(signal.title, 160);
  const artistName = cleanText(signal.artistName, 120);
  const message = cleanText(signal.message, 240);
  if (title) payload.title = title;
  if (artistName) payload.artistName = artistName;
  if (message) payload.message = message;

  if (type === 'play_count_updated') {
    if (!trackId) return null;
    payload.playCount = toNonNegativeInteger(signal.playCount);
    payload.delta = toNonNegativeInteger(signal.delta, 1);
  }

  if (
    type === 'listening_now_updated' ||
    type === 'radio_station_listeners_updated'
  ) {
    if (type === 'listening_now_updated' && !trackId) return null;
    payload.knownListenerCount = toNonNegativeInteger(signal.knownListenerCount);
    payload.anonymousListenerCount = toNonNegativeInteger(signal.anonymousListenerCount);
    payload.knownListenersPreview = normalizeListenerPreview(
      signal.knownListenersPreview,
    );
  }

  if (type === 'radio_room_presence_updated') {
    payload.playerIds = [
      ...new Set(
        (Array.isArray(signal.playerIds) ? signal.playerIds : [])
          .map(normalizePlayerHandle)
          .filter(Boolean),
      ),
    ];
    payload.count = toNonNegativeInteger(signal.count, payload.playerIds.length);
  }

  if (type === 'artist_connected') {
    const artistPlayerId = normalizePlayerHandle(
      signal.artistPlayerId || signal.linkedArtistPlayerId,
    );
    if (artistPlayerId) payload.artistPlayerId = artistPlayerId;
  }

  if (type === 'support_landed') {
    payload.supportType = cleanText(signal.supportType, 40);
  }

  return payload;
};

const getSignalKey = (signal) =>
  [
    signal.type,
    signal.trackId || '',
    signal.playCount ?? '',
    signal.at || '',
    signal.artistPlayerId || '',
  ].join(':');

const getMovementText = (signal, { currentTrackId, burstCount = 1 } = {}) => {
  if (!signal) return '';
  const isCurrent = !signal.trackId || signal.trackId === currentTrackId;
  const trackLabel = signal.title
    ? `${signal.title}${signal.artistName ? ` - ${signal.artistName}` : ''}`
    : 'Radio rotation';

  if (signal.type === 'play_count_updated') {
    const proofText =
      signal.playCount >= 3
        ? `${signal.playCount} qualified plays`
        : 'Qualified play counted';
    return isCurrent
      ? proofText
      : `${trackLabel}: ${proofText.charAt(0).toLowerCase()}${proofText.slice(1)}`;
  }
  if (signal.type === 'qualified_listen') {
    if (burstCount > 1) return `${burstCount} qualified plays just landed`;
    return isCurrent
      ? 'Qualified play just landed'
      : `${trackLabel}: qualified play`;
  }
  if (signal.type === 'feedback_landed') {
    return isCurrent ? 'Feedback landed' : `${trackLabel}: feedback landed`;
  }
  if (signal.type === 'support_landed') {
    return isCurrent ? 'Artist support landed' : `${trackLabel}: support landed`;
  }
  if (signal.type === 'artist_connected') {
    return isCurrent ? 'Artist profile connected' : `${trackLabel}: artist connected`;
  }
  if (signal.type === 'support_ready') {
    return isCurrent ? 'Artist support ready' : `${trackLabel}: support ready`;
  }
  if (signal.type === 'new_in_rotation') {
    return `New in rotation: ${trackLabel}`;
  }
  if (signal.type === 'rotation_refreshed') {
    return 'Radio rotation refreshed';
  }
  return '';
};

const getSocketFromContext = (socketContext) => {
  if (socketContext?.socket) return socketContext.socket;
  if (socketContext?.emit && socketContext?.on) return socketContext;
  return null;
};

const safeSocketEmit = (socket, eventName, payload) => {
  try {
    socket?.emit?.(eventName, payload);
  } catch (err) {
    // Presence signals are supplemental; playback and page rendering stay primary.
  }
};

const normalizePresenceStatus = (payload = {}) => ({
  playerId: normalizePlayerHandle(payload.handle || payload.playerId),
  status: cleanText(payload.status, 40),
  profilePhoto: cleanText(payload.profilePhoto, 2048),
  at: Date.now(),
});

export default function useJgRadioLiveSignals({
  capsule = null,
  isPlaying = false,
  enabled = true,
  onRefreshCatalog,
} = {}) {
  const socketContext = useSocket();
  const socket = getSocketFromContext(socketContext);
  const [playCountsByTrackId, setPlayCountsByTrackId] = useState({});
  const [listeningNowByTrackId, setListeningNowByTrackId] = useState({});
  const [stationListenerPresence, setStationListenerPresence] = useState(null);
  const [radioRoomPlayerIds, setRadioRoomPlayerIds] = useState([]);
  const [artistStatusByPlayerId, setArtistStatusByPlayerId] = useState({});
  const [movementSignal, setMovementSignal] = useState(null);
  const seenSignalKeysRef = useRef([]);
  const refreshRef = useRef({ at: 0, onRefreshCatalog });
  const qualifiedBurstRef = useRef({
    trackId: '',
    count: 0,
    expiresAt: 0,
  });

  useEffect(() => {
    refreshRef.current.onRefreshCatalog = onRefreshCatalog;
  }, [onRefreshCatalog]);

  useEffect(() => {
    if (!enabled || !socket?.emit) return undefined;
    safeSocketEmit(socket, HITLAB_RADIO_LIVE_SUBSCRIBE_EVENT, {});
    return () => {
      safeSocketEmit(socket, HITLAB_RADIO_LIVE_UNSUBSCRIBE_EVENT, {});
    };
  }, [enabled, socket]);

  useEffect(() => {
    if (!enabled || !socket?.on) return undefined;

    const requestCatalogRefresh = () => {
      const now = Date.now();
      if (now - refreshRef.current.at < REFRESH_THROTTLE_MS) return;
      refreshRef.current.at = now;
      refreshRef.current.onRefreshCatalog?.();
    };

    const handleSignal = (rawSignal) => {
      const signal = normalizeJgRadioLiveSignal(rawSignal);
      if (!signal) return;

      const signalKey = getSignalKey(signal);
      if (seenSignalKeysRef.current.includes(signalKey)) return;
      seenSignalKeysRef.current = [...seenSignalKeysRef.current, signalKey].slice(-80);

      if (signal.type === 'play_count_updated' && signal.trackId) {
        setPlayCountsByTrackId((current) => ({
          ...current,
          [signal.trackId]: signal.playCount,
        }));
      }

      if (signal.type === 'listening_now_updated' && signal.trackId) {
        setListeningNowByTrackId((current) => ({
          ...current,
          [signal.trackId]: signal,
        }));
      }

      if (signal.type === 'radio_station_listeners_updated') {
        setStationListenerPresence(signal);
      }

      if (signal.type === 'radio_room_presence_updated') {
        setRadioRoomPlayerIds(signal.playerIds || []);
      }

      if (MOVEMENT_SIGNAL_TYPES.has(signal.type)) {
        let burstCount = 1;
        if (signal.type === 'qualified_listen') {
          const now = Date.now();
          const burst = qualifiedBurstRef.current;
          if (burst.trackId === signal.trackId && burst.expiresAt > now) {
            burstCount = burst.count + 1;
          }
          qualifiedBurstRef.current = {
            trackId: signal.trackId || '',
            count: burstCount,
            expiresAt: now + 6000,
          };
        }
        setMovementSignal({
          ...signal,
          burstCount,
          expiresAt: Date.now() + SIGNAL_TTL_MS,
        });
      }

      if (CATALOG_REFRESH_SIGNAL_TYPES.has(signal.type)) {
        requestCatalogRefresh();
      }
    };

    const handlePlayerStatus = (payload) => {
      const status = normalizePresenceStatus(payload);
      if (!status.playerId) return;
      setArtistStatusByPlayerId((current) => ({
        ...current,
        [status.playerId]: status,
      }));
    };

    socket.on(HITLAB_RADIO_SIGNAL_EVENT, handleSignal);
    socket.on('player:status', handlePlayerStatus);
    return () => {
      socket.off?.(HITLAB_RADIO_SIGNAL_EVENT, handleSignal);
      socket.off?.('player:status', handlePlayerStatus);
    };
  }, [enabled, socket]);

  const activeTrackId =
    enabled &&
    isPlaying &&
    capsule?.kind === RADIO_QUEUE_KINDS.TRACK &&
    capsule?.trackId
      ? cleanText(capsule.trackId, 120)
      : '';

  useEffect(() => {
    if (!socket?.emit || !activeTrackId) return undefined;
    safeSocketEmit(socket, HITLAB_RADIO_TRACK_JOIN_EVENT, { trackId: activeTrackId });
    return () => {
      safeSocketEmit(socket, HITLAB_RADIO_TRACK_LEAVE_EVENT, { trackId: activeTrackId });
    };
  }, [activeTrackId, socket]);

  useEffect(() => {
    if (!socket?.emit || !activeTrackId) return undefined;
    const leaveActiveTrack = () => {
      safeSocketEmit(socket, HITLAB_RADIO_TRACK_LEAVE_EVENT, { trackId: activeTrackId });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        leaveActiveTrack();
      } else if (document.visibilityState === 'visible') {
        safeSocketEmit(socket, HITLAB_RADIO_TRACK_JOIN_EVENT, { trackId: activeTrackId });
      }
    };
    window.addEventListener('pagehide', leaveActiveTrack);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', leaveActiveTrack);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeTrackId, socket]);

  useEffect(() => {
    if (!movementSignal) return undefined;
    const timeoutMs = Math.max(0, movementSignal.expiresAt - Date.now());
    const timer = window.setTimeout(() => {
      setMovementSignal((current) =>
        current?.expiresAt === movementSignal.expiresAt ? null : current,
      );
    }, timeoutMs);
    return () => window.clearTimeout(timer);
  }, [movementSignal]);

  const currentTrackId = cleanText(capsule?.trackId, 120);
  const listenerPresence = listeningNowByTrackId[currentTrackId] || null;
  const linkedArtistPlayerId = normalizePlayerHandle(capsule?.linkedArtistPlayerId);
  const linkedArtistPresence = useMemo(() => {
    if (!linkedArtistPlayerId) {
      return {
        playerId: '',
        isOnline: false,
        isInRoom: false,
        status: 'unknown',
      };
    }
    const isInRoom = radioRoomPlayerIds.includes(linkedArtistPlayerId);
    const artistStatus = artistStatusByPlayerId[linkedArtistPlayerId] || {};
    const status = cleanText(artistStatus.status, 40);
    const isOnline =
      isInRoom ||
      status === 'online' ||
      status === 'idle' ||
      status === 'away';
    return {
      playerId: linkedArtistPlayerId,
      isOnline,
      isInRoom,
      status: isInRoom ? 'in_room' : status || 'unknown',
    };
  }, [artistStatusByPlayerId, linkedArtistPlayerId, radioRoomPlayerIds]);

  return {
    playCountsByTrackId,
    listenerPresence,
    stationListenerPresence,
    linkedArtistPresence,
    movementText: getMovementText(movementSignal, {
      currentTrackId,
      burstCount: movementSignal?.burstCount || 1,
    }),
  };
}
